import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Loader2, XCircle } from 'lucide-react'
import type { ClinicalAlert, DismissReason } from '../hooks/use-insights'

interface DismissDropdownProps {
  alert: ClinicalAlert
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: (reason: DismissReason, note?: string) => void
  isSubmitting?: boolean
}

const DISMISS_OPTIONS: { value: DismissReason; label: string }[] = [
  { value: 'already_aware', label: 'Já estou ciente' },
  { value: 'part_of_condition', label: 'Faz parte do quadro' },
  { value: 'already_resolved', label: 'Já foi resolvido' },
]

export function DismissDropdown({
  alert,
  open,
  onOpenChange,
  onConfirm,
  isSubmitting,
}: DismissDropdownProps) {
  const [selectedReason, setSelectedReason] = useState<DismissReason | null>(null)
  const [note, setNote] = useState('')

  const handleConfirm = () => {
    if (!selectedReason) return
    onConfirm(selectedReason, note.trim() || undefined)
    setSelectedReason(null)
    setNote('')
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <XCircle className="h-5 w-5" />
            Dispensar Alerta
          </DialogTitle>
          <DialogDescription>
            Selecione o motivo para dispensar este alerta.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <p className="text-sm text-muted-foreground line-clamp-2">
            {alert.insight_text}
          </p>

          <div className="space-y-2">
            {DISMISS_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => setSelectedReason(option.value)}
                className={`w-full text-left px-3 py-2.5 rounded-lg border text-sm transition-colors ${
                  selectedReason === option.value
                    ? 'border-teal-500 bg-teal-50 text-teal-800'
                    : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>

          <div>
            <textarea
              placeholder="Nota adicional (opcional)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="w-full min-h-[60px] px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Cancelar
          </Button>
          <Button onClick={handleConfirm} disabled={!selectedReason || isSubmitting}>
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Dispensar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
